/**
 * 0.36.721 — Repeater runtime, Ship B.
 *
 * Shared between PageContentRenderer (public /p/:slug) and
 * AdminFormPreview. Both call into this component, passing:
 *
 *   element             — the repeater element (with children +
 *                         config fields from Ship A's schema)
 *   ctx                 — { values, updateValue } (adapted from
 *                         the caller's render context)
 *   renderTemplateChild — callback (child, ctx) => ReactNode; the
 *                         caller supplies its own child renderer so
 *                         inputs behave identically to how they'd
 *                         render outside a repeater
 *
 * Behaviour (per Ship A spec):
 *
 *   - Empty template → placeholder box, no Add.
 *   - Template inputs rendered at top with element_ids scoped as
 *     `<repName>.__draft.<field>` (via rewriteElementIdsRecursive)
 *     so draft state never collides with same-named top-level
 *     fields elsewhere on the form.
 *   - Below inputs: Add button (or Save + Cancel when editing).
 *     Add is hidden when at max_count.
 *   - Below buttons: entry list. Each entry:
 *       * Optional title from entry_title_template (`{n}`
 *         substituted with 1-indexed position)
 *       * Two-column label/value block (no column headers)
 *       * Edit + Remove on their own row beneath
 *       * Currently-being-edited entry highlighted (blue tint)
 *
 * Add validates required fields; empty ones surface a small
 * error line below the buttons.
 *
 * Remove is immediate (no confirmation). Removing the entry
 * currently being edited also cancels the edit. Removing an
 * entry below the edited one shifts the editing index down.
 */
import React, { useState } from 'react';
import buttonStyles from './Button.module.css';
import {
  rewriteElementIdsRecursive,
  collectTemplateFieldNames,
  collectTemplateFieldLabels,
  clearDraft,
  loadEntryIntoDraft,
  validateDraft,
  readEntries,
  readEditingIdx,
  formatEntryValue,
} from '../utils/repeater';

function EntryCard({
  el, entry, idx, template, fieldNames, fieldLabels,
  isEditing, canRemove, onEdit, onRemove,
}) {
  const titleTpl = el.entry_title_template || '';
  const title = titleTpl
    ? titleTpl.replace(/\{n\}/g, String(idx + 1))
    : '';
  return (
    <div style={{
      // 0.44.74 - Defined tokens. --color-primary-* never existed (the
      // Tailwind fallbacks rendered), and grey-1200 drew a near-black
      // border where the fallback shows a light grey was meant.
      border: isEditing
        ? '2px solid var(--color-blue-800)'
        : '1px solid var(--color-text-300)',
      background: isEditing
        ? 'var(--color-blue-100)'
        : 'transparent',
      borderRadius: 'var(--radius-sm)',
      padding: 'var(--space-4)',
      marginBottom: 'var(--space-3)',
      transition: 'background 120ms ease, border-color 120ms ease',
    }}>
      {title && (
        <div style={{
          fontWeight: 600,
          fontSize: 'var(--font-size-base)',
          marginBottom: 12,
          color: 'var(--color-text-800)',
        }}>{title}</div>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: 16,
        rowGap: 8,
      }}>
        {fieldNames.map((fname) => (
          <React.Fragment key={fname}>
            <div style={{
              fontWeight: 500,
              color: 'var(--color-text-700)',
              alignSelf: 'start',
            }}>{fieldLabels[fname]}</div>
            <div style={{
              color: 'var(--color-text-800)',
              alignSelf: 'start',
              wordBreak: 'break-word',
            }}>{formatEntryValue(entry[fname])}</div>
          </React.Fragment>
        ))}
      </div>
      <div style={{
        marginTop: 16,
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
      }}>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.tertiary}`}
          disabled={isEditing}
          onClick={() => onEdit(idx)}>
          {el.edit_button_label ?? 'Edit'}
        </button>
        {canRemove && (
          <button
            type="button"
            // 0.44.74 - The canonical danger button (it exists since
            // 0.37.17); the inline red #dc2626 with a 1px border made
            // a 38px button with no hover.
            className={`${buttonStyles.button} ${buttonStyles.danger}`}
            onClick={() => onRemove(idx)}>
            {el.remove_button_label ?? 'Remove'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function RepeaterRuntime({ element: el, ctx, renderTemplateChild }) {
  const [validationError, setValidationError] = useState('');
  const repName = el.element_id || el.name;
  const template = Array.isArray(el.children) ? el.children : [];

  // Empty template — nothing to add. Render a small notice so admin
  // sees where the widget will appear once fields are added.
  if (!repName || template.length === 0) {
    return (
      <div style={{
        border: '1px dashed var(--color-text-400)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-4)',
        background: 'var(--color-bg-alt)',
      }}>
        <div style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-600)',
          fontStyle: 'italic',
        }}>
          {repName
            ? `Repeater (${repName}) has no template fields yet. Add fields inside the repeater in the form editor to enable it.`
            : 'Repeater has no element_id. Set one in the form editor to enable it.'}
        </div>
      </div>
    );
  }

  const fieldNames = collectTemplateFieldNames(template);
  const fieldLabels = collectTemplateFieldLabels(template);
  const entries = readEntries(ctx.values, repName);
  const editingIdx = readEditingIdx(ctx.values, repName);
  const isEditing = editingIdx >= 0 && editingIdx < entries.length;

  const minCount = Math.max(0, Number(el.min_count) || 0);
  const maxCount = Math.max(1, Number(el.max_count) || 99);
  const atMin = entries.length <= minCount;
  const atMax = entries.length >= maxCount;

  const draftPrefix = `${repName}.__draft.`;
  const scopedTemplate = template.map(c => rewriteElementIdsRecursive(c, draftPrefix));

  // Build a fresh entry object from the current draft values.
  const buildEntry = () => {
    const obj = {};
    for (const fname of fieldNames) {
      const v = ctx.values[`${draftPrefix}${fname}`];
      obj[fname] = v == null ? '' : v;
    }
    return obj;
  };

  const handleAdd = () => {
    const { valid, missing } = validateDraft(ctx.values, repName, template);
    if (!valid) {
      const labels = missing.map(n => fieldLabels[n] || n).join(', ');
      setValidationError(`Please fill in the required field(s): ${labels}`);
      return;
    }
    setValidationError('');
    ctx.updateValue(repName, [...entries, buildEntry()]);
    clearDraft(ctx.updateValue, repName, fieldNames);
    ctx.updateValue(`${repName}.__editing`, -1);
  };

  const handleSave = () => {
    if (!isEditing) return;
    const { valid, missing } = validateDraft(ctx.values, repName, template);
    if (!valid) {
      const labels = missing.map(n => fieldLabels[n] || n).join(', ');
      setValidationError(`Please fill in the required field(s): ${labels}`);
      return;
    }
    setValidationError('');
    const nextEntries = entries.slice();
    nextEntries[editingIdx] = buildEntry();
    ctx.updateValue(repName, nextEntries);
    clearDraft(ctx.updateValue, repName, fieldNames);
    ctx.updateValue(`${repName}.__editing`, -1);
  };

  const handleCancel = () => {
    setValidationError('');
    clearDraft(ctx.updateValue, repName, fieldNames);
    ctx.updateValue(`${repName}.__editing`, -1);
  };

  const handleEdit = (idx) => {
    setValidationError('');
    loadEntryIntoDraft(ctx.updateValue, repName, fieldNames, entries[idx] || {});
    ctx.updateValue(`${repName}.__editing`, idx);
  };

  const handleRemove = (idx) => {
    setValidationError('');
    const next = entries.filter((_, i) => i !== idx);
    ctx.updateValue(repName, next);
    // Adjust the editing pointer.
    if (editingIdx === idx) {
      // We just deleted the entry the citizen was editing — cancel edit.
      clearDraft(ctx.updateValue, repName, fieldNames);
      ctx.updateValue(`${repName}.__editing`, -1);
    } else if (editingIdx > idx) {
      // Deleted entry sat above the edited one — shift index down.
      ctx.updateValue(`${repName}.__editing`, editingIdx - 1);
    }
  };

  return (
    <div>
      {/* Template inputs (draft-scoped) */}
      <div style={{
        border: '1px solid var(--color-text-300)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-3)',
      }}>
        {scopedTemplate.map((child) => renderTemplateChild(child, ctx))}
        <div style={{
          marginTop: 8,
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          {isEditing ? (
            <>
              <button
                type="button"
                className={`${buttonStyles.button} ${buttonStyles.primary}`}
                onClick={handleSave}>
                {el.save_button_label || 'Save'}
              </button>
              <button
                type="button"
                className={`${buttonStyles.button} ${buttonStyles.tertiary}`}
                onClick={handleCancel}>
                {el.cancel_button_label || 'Cancel'}
              </button>
            </>
          ) : (
            !atMax && (
              <button
                type="button"
                className={`${buttonStyles.button} ${buttonStyles.primary}`}
                onClick={handleAdd}>
                {el.add_button_label || '+ Add'}
              </button>
            )
          )}
          {atMax && !isEditing && (
            <span style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-600)',
              fontStyle: 'italic',
            }}>
              Maximum reached ({maxCount}).
            </span>
          )}
        </div>
        {validationError && (
          // 0.44.74 - The field error text style (error-dark, 16/600).
          <div className="field-error-text" style={{ marginTop: 8 }}>{validationError}</div>
        )}
      </div>

      {/* Entry list */}
      {entries.map((entry, idx) => (
        <EntryCard
          key={idx}
          el={el}
          entry={entry}
          idx={idx}
          template={template}
          fieldNames={fieldNames}
          fieldLabels={fieldLabels}
          isEditing={editingIdx === idx}
          canRemove={!atMin}
          onEdit={handleEdit}
          onRemove={handleRemove}
        />
      ))}
    </div>
  );
}
